import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ChangeDetectorRef, inject } from '@angular/core';
import { RouterModule, Event as RouterEvent, NavigationEnd } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NavMenu } from '../models';
import { AuthUser } from '../../auth/models';
import { Brand, Region } from '../../core/organizations/page/models/organization.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppPermissions, UserType } from '../../auth/enums';
import { AuthorizeDirective } from '../../auth/directives';
import { AuthService } from '../../auth/services';

import { SelectionService } from '../../shared/services';
import { TokenService } from '../../auth/services/token.service';
import { Endpoints } from '@core/api';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ApplicationComponent } from '../../shared/base/application-component.component';

@Component({
    selector: 'app-sidenav',
    standalone: true,
    imports: [
        RouterModule,
        CommonModule,
        MatDialogModule,
        MatMenuModule,
        MatButtonModule,
        MatIconModule,
        TranslateModule,
        MatTooltipModule,
        AuthorizeDirective,
        FormsModule,
        MatSelectModule,
    ],
    templateUrl: './sidenav.component.html',
    styleUrl: './sidenav.component.sass',
})
export class SidenavComponent extends ApplicationComponent {
    private auth = inject(AuthService);
    private cdr = inject(ChangeDetectorRef);
    private tokenService = inject(TokenService);
    private selectionService = inject(SelectionService);

    @Input() isCollapsed = true;
    @Output() collapseChanged = new EventEmitter<boolean>();
    activeRoute = '';

    activeSubmenu: NavMenu | null = null;
    groupedMenu: Record<string, NavMenu[]> = {};
    authorizedItems = new Set<string>();
    groupOrder = ['Main', 'Admin Tools', 'Analysis', 'Tasks', 'CRM', 'Cross-Platform'];

    user: AuthUser | null = null;
    brands: Brand[] = [];
    regions: Region[] = [];
    selectedBrandId: number | null = null;
    selectedRegionId: number | null = null;

    private authSub!: Subscription;
    private stateSub!: Subscription;

    constructor() {
        super();
        this.activeRoute = this.router.url;
        this.router.events.subscribe((event: RouterEvent) => {
            if (event instanceof NavigationEnd) {
                this.activeRoute = event.urlAfterRedirects;

                // Only auto-open if we don't have one open manually
                let isCurrentSubmenuActive = false;
                if (this.activeSubmenu) {
                    isCurrentSubmenuActive =
                        this.activeSubmenu.children?.some(
                            (child) => child.route && this.activeRoute.includes(child.route),
                        ) || false;
                }

                if (!this.activeSubmenu || !isCurrentSubmenuActive) {
                    this.menu.forEach((item) => {
                        if (this.hasChildren(item)) {
                            const isActive = item.children?.some(
                                (child) => child.route && this.activeRoute.includes(child.route),
                            );
                            if (isActive) {
                                this.activeSubmenu = item;
                            }
                        }
                    });
                }
                this.cdr.markForCheck();
            }
        });

        this.stateSub = this.selectionService.state$.subscribe((state) => {
            this.selectedBrandId = state.brandId;
            this.selectedRegionId = state.regionId;
            this.updateRegionsDropdown();
            this.cdr.markForCheck();
        });

        this.authSub = this.tokenService.getSession().subscribe((auth) => {
            if (auth?.user) {
                this.user = auth.user;
                this.cdr.markForCheck();
                if (this.user.user_type === 'ORGANIZATION') {
                    this.loadBrands();
                }
            }
        });
    }

    protected override onDestroy() {
        if (this.authSub) this.authSub.unsubscribe();
        if (this.stateSub) this.stateSub.unsubscribe();
    }

    onBrandChange(brandId: number) {
        this.selectedBrandId = Number(brandId);
        this.updateRegionsDropdown();

        this.selectedRegionId = this.regions.length > 0 ? (this.regions[0].id ?? null) : null;
        const brand = this.brands.find((b) => b.id === this.selectedBrandId);
        const region = this.regions.find((r) => r.id === this.selectedRegionId);
        this.selectionService.setSelection(
            this.selectedBrandId,
            this.selectedRegionId,
            brand?.name,
            region?.name,
        );
    }

    onRegionChange(regionId: number) {
        this.selectedRegionId = Number(regionId);
        const brand = this.brands.find((b) => b.id === this.selectedBrandId);
        const region = this.regions.find((r) => r.id === this.selectedRegionId);
        this.selectionService.setSelection(
            this.selectedBrandId,
            this.selectedRegionId,
            brand?.name,
            region?.name,
        );
    }

    private updateRegionsDropdown() {
        if (!this.selectedBrandId || this.brands.length === 0) {
            this.regions = [];
            return;
        }
        const brand = this.brands.find((b) => b.id === this.selectedBrandId);
        this.regions = brand ? brand.regions || [] : [];
    }

    protected loadBrands(): void {
        this.request<Brand[]>(this.http.get<Brand[]>(`${Endpoints.USER_BRANDS}`), {
            showToast: false,
            onSuccess: (data) => {
                if (data && data.length > 0) {
                    this.brands = data;
                    const currentSelection = this.selectionService.getCurrentSelection();

                    let targetBrandId = currentSelection.brandId;
                    let targetRegionId = currentSelection.regionId;

                    const brandExists = this.brands.some((b) => b.id === targetBrandId);
                    if (!brandExists) {
                        targetBrandId = this.brands[0].id ?? null;
                        const defaultRegions = this.brands[0].regions || [];
                        targetRegionId =
                            defaultRegions.length > 0 ? (defaultRegions[0].id ?? null) : null;
                    } else {
                        const brand = this.brands.find((b) => b.id === targetBrandId);
                        const regionExists = (brand?.regions || []).some(
                            (r: Region) => r.id === targetRegionId,
                        );
                        if (!regionExists) {
                            targetRegionId =
                                brand?.regions && brand.regions.length > 0
                                    ? (brand.regions[0].id ?? null)
                                    : null;
                        }
                    }

                    if (
                        targetBrandId !== currentSelection.brandId ||
                        targetRegionId !== currentSelection.regionId ||
                        !currentSelection.brandName ||
                        !currentSelection.regionName
                    ) {
                        const brand = this.brands.find((b) => b.id === targetBrandId);
                        const region = (brand?.regions || []).find(
                            (r: Region) => r.id === targetRegionId,
                        );
                        this.selectionService.setSelection(
                            targetBrandId,
                            targetRegionId,
                            brand?.name,
                            region?.name,
                        );
                    } else {
                        this.updateRegionsDropdown();
                    }
                } else {
                    this.brands = [];
                    this.regions = [];
                }
                this.cdr.markForCheck();
            },
            onError: () => {
                this.brands = [];
                this.regions = [];
                this.cdr.markForCheck();
            },
        });
    }

    protected override onViewInit() {
        this.menu.forEach((item) => {
            const groupName = item.group || 'Main';
            if (!this.groupedMenu[groupName]) {
                this.groupedMenu[groupName] = [];
            }
            this.groupedMenu[groupName].push(item);

            this.auth.isAuthorize(item).subscribe((access) => {
                if (access) {
                    this.authorizedItems.add(item.label);
                } else {
                    this.authorizedItems.delete(item.label);
                }
            });
        });

        this.checkActiveSubmenu();
    }

    checkActiveSubmenu() {
        if (this.activeSubmenu) return;
        this.menu.forEach((item) => {
            if (this.hasChildren(item)) {
                const isActive = item.children?.some(
                    (child) => child.route && this.activeRoute.includes(child.route),
                );
                if (isActive) {
                    this.activeSubmenu = item;
                }
            }
        });
    }

    isGroupVisible(groupItems: NavMenu[]): boolean {
        return groupItems.some((item) => this.authorizedItems.has(item.label));
    }

    openSubmenu(item: NavMenu) {
        if (this.hasChildren(item)) {
            this.activeSubmenu = item;
        }
    }

    closeSubmenu() {
        this.activeSubmenu = null;
    }

    menu: NavMenu[] = [
        // Main
        {
            label: 'Dashboard',
            icon: 'fa-solid fa-gauge',
            route: '/dashboard',
            userTypes: [UserType.INTERNAL, UserType.ORGANIZATION],
            group: 'Main',
        },
        {
            label: 'Organizations',
            icon: 'fa-solid fa-building',
            route: '/dashboard/organizations',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_ORGANIZATION],
            group: 'Main',
        },
        {
            label: 'Categories',
            icon: 'fa-solid fa-list',
            route: '/dashboard/categories',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_CATEGORIES],
            group: 'Main',
        },
        {
            label: 'Platforms',
            icon: 'fa-solid fa-layer-group',
            route: '/dashboard/platforms',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_PLATFORMS],
            group: 'Main',
        },
        {
            label: 'JSON Templates',
            icon: 'fa-solid fa-file-code',
            route: '/dashboard/json-templates',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_JSON_TEMPLATES],
            group: 'Main',
        },
        {
            label: 'LLM Provider',
            icon: 'fa-solid fa-microchip',
            route: '/dashboard/llm-providers',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_LLM_PROVIDERS],
            group: 'Main',
        },
        {
            label: 'API Provider',
            icon: 'fa-solid fa-network-wired',
            route: '/dashboard/api-providers',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_API_PROVIDERS],
            group: 'Main',
        },

        // Admin Tools
        {
            label: 'Market Data',
            icon: 'fa-solid fa-database',
            route: '/dashboard/market-data/dump',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_MARKET_DATA_DUMP],
            group: 'Admin Tools',
        },
        {
            label: 'Data Import',
            icon: 'fa-solid fa-file-import',
            route: '/dashboard/market-data/imports',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_MARKET_DATA_DUMP],
            group: 'Admin Tools',
        },
        {
            label: 'Json Generation',
            icon: 'fa-solid fa-sitemap',
            route: '/dashboard/json-generation',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_JSON_GENERATION],
            group: 'Admin Tools',
        },
        {
            label: 'Gen Insights',
            icon: 'fa-solid fa-brain',
            route: '/dashboard/gen-insights',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_GEN_INSIGHTS],
            group: 'Admin Tools',
        },

        // Analysis
        {
            label: 'LLM Analysis',
            icon: 'fa-solid fa-chart-bar',
            route: '/dashboard/llm-analysis',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.VIEW_ANALYTICS],
            group: 'Analysis',
        },
        {
            label: 'API Analysis',
            icon: 'fa-solid fa-chart-line',
            route: '/dashboard/api-analysis',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.VIEW_ANALYTICS],
            group: 'Analysis',
        },

        // Tasks
        {
            label: 'Executions',
            icon: 'fa-solid fa-server',
            route: '/dashboard/executions',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_EXECUTIONS],
            group: 'Tasks',
        },
        {
            label: 'Tasks',
            icon: 'fa-solid fa-tasks',
            route: '/dashboard/tasks',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_TASKS],
            group: 'Tasks',
        },

        // CRM
        {
            label: 'Users',
            icon: 'fa-solid fa-users',
            route: '/dashboard/users',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_USER],
            group: 'CRM',
        },
        {
            label: 'Roles',
            icon: 'fa-solid fa-user-shield',
            route: '/dashboard/roles',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_ROLES],
            group: 'CRM',
        },
        {
            label: 'Schedulers',
            icon: 'fa-solid fa-calendar-days',
            route: '/dashboard/schedulers',
            userTypes: [UserType.INTERNAL],
            permissions: [AppPermissions.READ_SCHEDULERS],
            group: 'CRM',
        },

        // Cross-Platform
        {
            label: 'Experience Cloud',
            icon: 'fa-solid fa-cloud',
            route: '',
            userTypes: [UserType.ORGANIZATION],
            permissions: [AppPermissions.READ_EXPERIANCE_DASHBOARD],
            group: 'Cross-Platform',
            children: [
                {
                    label: 'Dashboard',
                    icon: 'fa-solid fa-lightbulb',
                    route: '/dashboard/experience/insights',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_EXPERIANCE_DASHBOARD],
                },
                {
                    label: 'Action Center',
                    icon: 'fa-solid fa-clipboard-list',
                    route: '/dashboard/experience/action-dashboard',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_EXPERIANCE_DASHBOARD],
                },
                {
                    label: 'Risk Center',
                    icon: 'fa-solid fa-triangle-exclamation',
                    route: '/dashboard/experience/risk-center',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_RISK_CENTER],
                },
                {
                    label: 'Growth Lever',
                    icon: 'fa-solid fa-house-chimney',
                    route: '/dashboard/experience/positive',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_GROWTH_LEVER],
                },
                {
                    label: 'CRO Barriers',
                    icon: 'fa-solid fa-chart-line',
                    route: '/dashboard/experience/cro',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_CRO_BARRIERS],
                },
                {
                    label: 'Gap Landscapes',
                    icon: 'fa-solid fa-list',
                    route: '/dashboard/experience/category',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_GAP_LANDSCAPES],
                },
                {
                    label: 'Platform Audit',
                    icon: 'fa-solid fa-tags',
                    route: '/dashboard/experience/audit',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_PLATFORM_AUDIT],
                },
                {
                    label: 'Audience Audit',
                    icon: 'fa-solid fa-file-invoice',
                    route: '/dashboard/experience/reports',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_AUDIENCE_AUDIT],
                },
            ],
        },
        {
            label: 'Media Cloud',
            icon: 'fa-solid fa-photo-film',
            route: '',
            userTypes: [UserType.ORGANIZATION],
            permissions: [AppPermissions.READ_EXPERIANCE_DASHBOARD],
            group: 'Cross-Platform',
            children: [
                {
                    label: 'Campaign Setup',
                    icon: 'fa-solid fa-bullhorn',
                    route: '/dashboard/campaign-setup',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_EXPERIANCE_DASHBOARD],
                },
                {
                    label: 'Campaign',
                    icon: 'fa-solid fa-rectangle-ad',
                    route: '/dashboard/campaign',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_EXPERIANCE_DASHBOARD],
                },
                {
                    label: 'Action Center',
                    icon: 'fa-solid fa-bolt',
                    route: '/dashboard/media-action-center',
                    userTypes: [UserType.ORGANIZATION],
                    permissions: [AppPermissions.READ_EXPERIANCE_DASHBOARD],
                },
            ],
        },
    ];

    hasChildren(item: NavMenu): boolean {
        return Array.isArray(item.children) && item.children.length > 0;
    }

    getParentActiveState(item: NavMenu): boolean {
        if (!item.children) return false;
        return item.children.some((child) => this.activeRoute.includes(child.route ?? ''));
    }

    setActive(route: string) {
        this.activeRoute = route;
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
        this.collapseChanged.emit(this.isCollapsed);
    }

    // sidebar.component.ts
    expandedMenus = new Set<string>();

    toggleSubmenu(item: NavMenu): void {
        const label = item.label;

        if (this.expandedMenus.has(label)) {
            // If already expanded, collapse it
            this.expandedMenus.delete(label);
        } else {
            // Collapse all others
            this.expandedMenus.clear();
            this.expandedMenus.add(label);
        }
    }

    isExpanded(item: NavMenu): boolean {
        return this.expandedMenus.has(item.label);
    }
}
